import React from "react";
import { useSimulation } from "@/context/SimulationContext";
import { useI18n } from "@/context/LanguageContext";
import PhaseHeader from "@/components/PhaseHeader";
import PhaseFooter from "@/components/PhaseFooter";
import LabeledField from "@/components/LabeledField";
import HourlyRatePill from "@/components/HourlyRatePill";
import { Wrench, Package } from "lucide-react";
import ToggleField from "@/components/ToggleField";
import { customerBreakdown, FRAME_A_DS_LEN_M, FRAME_A_CP_LEN_M, FRAME_B_DS_LEN_M } from "@/lib/simulation-defaults";

function SubtotalRow({ label, value, testId, accent = false }) {
  return (
    <div
      className="mt-2 pt-2 border-t border-[color:var(--border)] flex items-center justify-between gap-3"
      data-testid={testId}
    >
      <span
        className={`text-[10px] font-mono uppercase tracking-[0.16em] ${
          accent ? "text-[color:var(--brand)]" : "text-[color:var(--text-mute)]"
        }`}
      >
        {label}
      </span>
      <span
        className={`font-mono font-semibold tabular-num ${
          accent ? "text-[color:var(--brand)] text-base" : "text-[color:var(--text)] text-sm"
        }`}
      >
        € {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

/** Result chip — shows computed € per door with strong visual presence */
function ResultChip({ label, value, testId }) {
  return (
    <div className="bg-[color:var(--surface-2)] border border-[color:var(--border-strong)] rounded px-3 py-2 flex items-center justify-between" data-testid={testId}>
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[color:var(--text-mute)]">{label}</span>
      <span className="text-sm font-bold font-mono tabular-num text-[color:var(--text)]">
        € {value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}

export default function Phase2CustomerCosts() {
  const { state, updateCustomer, updateTrailer } = useSimulation();
  const { t } = useI18n();
  const c = state.customer;
  const sidesDiffer = !!state.trailer.sides_differ;
  const doors = Number(state.trailer.doors_per_trailer) || 2;
  const hourlyRate = Number(state.trailer.labor_hourly_rate) || 12;
  const thickness = Number(state.trailer.profile_thickness_mm) || 85;

  const cb = customerBreakdown(c, sidesDiffer, doors, hourlyRate, state.trailer);
  const runningTotal = cb.total;

  const fmtEur = (v) => `€ ${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="wizard-phase phase-in max-w-7xl mx-auto px-4 sm:px-6">
      <PhaseHeader
        eyebrow={t("phases.p2")}
        title={t("customer.title")}
        subtitle={t("customer.subtitle")}
      />

      <div className="wizard-body">
        <div className="wizard-body-scroll grid grid-cols-1 lg:grid-cols-2 gap-3 auto-rows-min">
          {/* Profiles */}
          <section className="card-tech p-3 lg:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">
                  {t("customer.profiles")}
                </h3>
              </div>
              <ToggleField
                id="cust-sides-toggle"
                label={t("customer.sidesDiffer")}
                checked={sidesDiffer}
                onCheckedChange={(v) => updateTrailer({ sides_differ: v })}
                testId="cust-sides-toggle"
              />
            </div>

            {!sidesDiffer && (
              <div>
                <LabeledField
                  id="c-ds-price"
                  label={`${t("customer.doorsealing")} ${thickness}mm`}
                  value={c.doorsealing_price_per_m}
                  onChange={(v) => updateCustomer({ doorsealing_price_per_m: v, right_doorsealing_price_per_m: v })}
                  unit="€/m"
                  testId="cust-doorsealing-price"
                />
              </div>
            )}

            {sidesDiffer && (
              <div className="grid grid-cols-2 gap-2" data-testid="cust-sides-split">
                <div className="border border-[color:var(--brand)]/30 bg-[color:var(--brand-soft)] rounded p-2 space-y-2">
                  <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[color:var(--brand)] leading-tight font-bold">
                    ◄ Porta Esq. · {FRAME_A_DS_LEN_M}m + {FRAME_A_CP_LEN_M}m CP
                  </div>
                  <LabeledField
                    id="c-ds-l"
                    label={`${t("customer.doorsealing")} ${thickness}mm`}
                    value={c.doorsealing_price_per_m}
                    onChange={(v) => updateCustomer({ doorsealing_price_per_m: v })}
                    unit="€/m"
                    testId="cust-doorsealing-price"
                  />
                  <LabeledField
                    id="c-cp-l"
                    label={`${t("customer.centerprofile")} ${thickness}mm`}
                    value={c.centerprofile_price_per_m}
                    onChange={(v) => updateCustomer({ centerprofile_price_per_m: v })}
                    unit="€/m"
                    testId="cust-centerprofile-price"
                  />
                </div>
                <div className="border border-[color:var(--border)] rounded p-2 space-y-2">
                  <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[color:var(--text-mute)] leading-tight font-bold">
                    Porta Dir. · {FRAME_B_DS_LEN_M}m ►
                  </div>
                  <LabeledField
                    id="c-ds-r"
                    label={`${t("customer.doorsealing")} ${thickness}mm`}
                    value={c.right_doorsealing_price_per_m}
                    onChange={(v) => updateCustomer({ right_doorsealing_price_per_m: v })}
                    unit="€/m"
                    testId="cust-doorsealing-price-right"
                  />
                  <LabeledField
                    id="c-cp-r"
                    label={`${t("customer.centerprofile")} ${thickness}mm`}
                    value={c.right_centerprofile_price_per_m}
                    onChange={(v) => updateCustomer({ right_centerprofile_price_per_m: v })}
                    unit="€/m"
                    testId="cust-centerprofile-price-right"
                  />
                </div>
              </div>
            )}

            {sidesDiffer && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <ResultChip label="Porta Esq." value={cb.frameAProfiles} testId="cust-frame-a-cost" />
                <ResultChip label="Porta Dir." value={cb.frameBProfiles} testId="cust-frame-b-cost" />
              </div>
            )}

            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("customer.profiles")}`}
              value={cb.profiles}
              testId="cust-subtotal-profiles"
            />
          </section>

          {/* Labor */}
          <section className="card-tech p-3 lg:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5 text-[color:var(--brand)]" />
                <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">{t("customer.labor")}</h3>
              </div>
              <HourlyRatePill testId="cust-hourly-rate-pill" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <LabeledField
                  id="c-cut-t"
                  label={t("customer.cuttingTime")}
                  value={c.cutting_time_min}
                  onChange={(v) => updateCustomer({ cutting_time_min: v })}
                  unit="min"
                  testId="cust-cutting-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="cust-cutting-cost-hint">
                    {fmtEur(cb.cuttingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
              <div>
                <LabeledField
                  id="c-gl-t"
                  label={t("customer.gluing")}
                  value={c.corner_gluing_time_min}
                  onChange={(v) => updateCustomer({ corner_gluing_time_min: v })}
                  unit="min"
                  testId="cust-gluing-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="cust-gluing-cost-hint">
                    {fmtEur(cb.gluingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
              <div>
                <LabeledField
                  id="c-mt-t"
                  label={t("customer.mounting")}
                  value={c.mounting_time_min}
                  onChange={(v) => updateCustomer({ mounting_time_min: v })}
                  unit="min"
                  testId="cust-mounting-time"
                />
                <div className="mt-1.5 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-2 py-1 text-center">
                  <span className="text-xs font-bold font-mono tabular-num text-[color:var(--text)]" data-testid="cust-mounting-cost-hint">
                    {fmtEur(cb.mountingCost)}
                  </span>
                  <span className="text-[9px] text-[color:var(--text-mute)] ml-1">/ porta</span>
                </div>
              </div>
            </div>

            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("customer.labor")} (× ${doors})`}
              value={cb.labor}
              testId="cust-subtotal-labor"
            />
          </section>

          {/* Steel band */}
          <section className="card-tech p-3 lg:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-3.5 h-3.5 text-[color:var(--brand)]" />
              <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">{t("customer.steelband")}</h3>
            </div>

            {/* Row 1 — €/m + two lengths */}
            <div className="grid grid-cols-3 gap-2">
              <LabeledField
                id="c-sb-price-m"
                label={t("customer.steelbandPricePerM")}
                value={c.steelband_price_per_m}
                onChange={(v) => updateCustomer({ steelband_price_per_m: v })}
                unit="€/m"
                testId="cust-steelband-price-per-m"
              />
              <LabeledField
                id="c-sb-len-a"
                label={t("customer.steelbandLengthA")}
                value={c.steelband_length_a_m}
                onChange={(v) => updateCustomer({ steelband_length_a_m: v })}
                unit="m"
                testId="cust-steelband-length-a"
              />
              <LabeledField
                id="c-sb-len-b"
                label={t("customer.steelbandLengthB")}
                value={c.steelband_length_b_m}
                onChange={(v) => updateCustomer({ steelband_length_b_m: v })}
                unit="m"
                testId="cust-steelband-length-b"
              />
            </div>

            {/* Computed steel-band material cost (excludes drilling + rivets). Shows the
                formula so the salesperson can explain the €/m × length breakdown. */}
            <div
              className="mt-2 bg-[color:var(--brand-soft)] border border-[color:var(--brand)]/30 rounded px-3 py-2 flex items-center justify-between gap-3"
              data-testid="cust-steelband-formula"
            >
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[color:var(--text-mute)] truncate">
                {(Number(c.steelband_price_per_m) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/m
                {" × "}
                {(Number(c.steelband_length_a_m) || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}
                {" + "}
                {(Number(c.steelband_length_b_m) || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}
                {" = "}
                {cb.steelbandLengthTotalM.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })} m
              </span>
              <span className="text-sm font-bold font-mono tabular-num text-[color:var(--brand)] shrink-0">
                {fmtEur(cb.steelbandBaseCost)}
              </span>
            </div>

            {/* Row 2 — drilling (time) + popniets (€) */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <LabeledField
                id="c-sb-drill"
                label={t("customer.drilling")}
                value={c.drilling_time_min}
                onChange={(v) => updateCustomer({ drilling_time_min: v })}
                unit="min"
                testId="cust-drilling-time"
              />
              <LabeledField
                id="c-sb-pop"
                label={t("customer.popniets")}
                value={c.popniets_cost}
                onChange={(v) => updateCustomer({ popniets_cost: v })}
                unit="€"
                testId="cust-popniets-cost"
              />
            </div>

            {/* Drilling formula pill — mirrors the steel-band one so the
                salesperson can explain: time (min) ÷ 60 × €/h = € per frame. */}
            <div
              className="mt-2 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-3 py-1.5 flex items-center justify-between gap-3"
              data-testid="cust-drilling-formula"
            >
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[color:var(--text-mute)] truncate">
                {(Number(c.drilling_time_min) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} min
                {" ÷ 60 × "}
                {(Number(hourlyRate) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/h
              </span>
              <span className="text-sm font-bold font-mono tabular-num text-[color:var(--text)] shrink-0">
                {fmtEur(cb.drillingCost)}
              </span>
            </div>

            {/* Per-door subtotal (steel band material + drilling + rivets) */}
            <div className="mt-2 bg-[color:var(--surface-2)] border border-[color:var(--border)] rounded px-3 py-2 flex items-center justify-between" data-testid="cust-steelband-per-door">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[color:var(--text-mute)]">
                Custo / porta
              </span>
              <span className="text-sm font-bold font-mono tabular-num text-[color:var(--text)]">
                {fmtEur(cb.steelbandPerFrame)}
              </span>
            </div>

            <SubtotalRow
              label={`${t("common.subtotal")} · ${t("customer.steelband")} (× ${doors})`}
              value={cb.steelband}
              testId="cust-subtotal-steelband"
            />
          </section>

          {/* Totals */}
          <section className="card-tech p-3 lg:p-4 border-[color:var(--danger)]/50" data-testid="cust-totals-card">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-3.5 h-3.5 text-[color:var(--danger)]" />
              <h3 className="font-display font-semibold text-[color:var(--text)] text-sm">
                {t("customer.totalPrice")}
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <ResultChip label="Porta Esq." value={cb.frameATotal} testId="cust-frame-a-total" />
              <ResultChip label="Porta Dir." value={cb.frameBTotal} testId="cust-frame-b-total" />
            </div>

            <SubtotalRow
              label={`${t("customer.totalPrice")}`}
              value={runningTotal}
              testId="cust-running-total"
              accent
            />
          </section>
        </div>
      </div>

      <PhaseFooter />
    </div>
  );
}
